import React from 'react';
import millify from 'millify';
import type { Maybe } from '@soundxyz/utils';
import { shortenHash } from '@soundxyz/utils/const';
import { styled } from '@components-shared/src/stitches';
import type { Pathname } from '../link/AccessibleLink';
import { AccessibleLink } from '../link/AccessibleLink';

export const AVATAR_SIZE_USER_HOVER_CARD_CONTENT = 74;

interface MoleculeUserHoverCardContentProps {
  user: {
    id: string;
    publicAddress: string;
    webappUri: Pathname;
    username: string;
    followerCount: number;
    followingCount: number;
    nftsOwned: number;
    artist?: Maybe<{ numReleases: number; bannerImage?: Maybe<{ url: string }> }>;
    avatar?: Maybe<{ url: string }>;
    creditAllocations: unknown[];
    bannerImage?: Maybe<{ url: string }>;
  };
  followButton: React.ReactNode;
  verified: React.ReactNode;
  avatar: React.ReactNode;
}

export const UserHoverCardContentUI = ({
  user,
  followButton,
  verified,
  avatar: avatarProp,
}: MoleculeUserHoverCardContentProps) => {
  const {
    publicAddress,
    username,
    followerCount,
    followingCount,
    nftsOwned,
    artist,
    creditAllocations,
    webappUri,
    bannerImage,
  } = user;

  const hasDisplayName = username !== shortenHash(publicAddress, 5, 4);
  const soundsCount = (artist ? artist?.numReleases : creditAllocations.length) || 0;
  const backgroundImageUrl =
    artist?.bannerImage?.url ?? bannerImage?.url ?? '/images/sound_letter_s.svg';

  return (
    <div className="h-fit w-[300px]">
      <Header
        css={{
          backgroundImage: `url(${backgroundImageUrl})`,
          backgroundSize: 'cover',
        }}
      />
      <AvatarWrapper>{avatarProp}</AvatarWrapper>
      <div className="flex flex-col gap-[22px] p-[14px]">
        <div className="flex justify-between">
          <AccessibleLink pathnameUri={webappUri}>
            <UserInfo>
              <NameContainer>
                <h1>{username}</h1>
                {artist && verified}
              </NameContainer>
              {hasDisplayName && <p>{shortenHash(publicAddress, 5, 4)}</p>}
            </UserInfo>
          </AccessibleLink>
          {followButton}
        </div>
        <div className="flex justify-between">
          {!!soundsCount && (
            <Stat>
              <div>{millify(soundsCount)}</div>
              <p>Sound{soundsCount > 1 && 's'}</p>
            </Stat>
          )}
          <Stat>
            <div>{millify(nftsOwned)}</div>
            <p>Collected</p>
          </Stat>
          <Stat>
            <div>{millify(followingCount)}</div>
            <p>Following</p>
          </Stat>
          <Stat>
            <div>{millify(followerCount)}</div>
            <p>Followers</p>
          </Stat>
        </div>
      </div>
    </div>
  );
};

const Header = styled('div', {
  display: 'flex',
  width: '100%',
  height: 76,
  borderRadius: '8px 8px 0 0',
  backgroundPosition: 'center',
});

const AvatarWrapper = styled('div', {
  justifyContent: 'center',
  alignItems: 'center',
  width: AVATAR_SIZE_USER_HOVER_CARD_CONTENT,
  height: AVATAR_SIZE_USER_HOVER_CARD_CONTENT,
  borderRadius: 8,
  border: '4px solid $white',
  background: '$white',
  marginTop: -56,
  marginX: 14,
});

const UserInfo = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: 4,
  p: {
    base: 'M',
    color: '$neutral400',
  },
});

const NameContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  h1: {
    base: 'L',
    color: '$white',
    fontStyle: 'medium',
    maxWidth: 118,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

const Stat = styled('div', {
  div: {
    base: 'M',
    fontStyle: 'semibold',
    color: '$white',
  },

  p: {
    base: 'S',
    fontStyle: 'medium',
    color: '$neutral400',
  },
});
